import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  ReportSchool,
  SchoolTier,
  TargetTest,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.types';

import { Box, Skeleton, SxProps, Theme, Tooltip, Typography } from '@mui/material';

const STRINGS = appStrings.modules.satActBooster.targets.fitTable;

type FitSchool = ReportSchool;
type MidRange = { low: number; high: number } | null;

/**
 * A selected school plus its fit data. `computed` is null while the data loads, and stays
 * null for a school with no linked institution — there is no profile to tier such a school.
 */
export type FitRow = Readonly<{
  name: string;
  computed: FitSchool | null;
  /** No fit data yet and a read is in flight — shimmer this row's data cells. */
  isPending: boolean;
}>;

type SchoolFitTableProps = Readonly<{
  rows: readonly FitRow[];
  /** Entered score for the plan's test; shown in the "Your score" column. */
  currentScore: number | null;
  /** The plan's test, decided by the report (the stronger test for a "Both" taker). */
  test: TargetTest;
}>;

// Subtle background + matching text per tier, so the three states read distinctly.
const TIER_COLORS: Record<
  SchoolTier,
  { bg: keyof Theme['backrs']['colors']; text: keyof Theme['backrs']['colors'] }
> = {
  safety: { bg: 'Colors/Background/bg-success-primary', text: 'Colors/Text/text-success-primary' },
  target: { bg: 'Colors/Background/bg-warning-primary', text: 'Colors/Text/text-warning-primary' },
  reach: { bg: 'Colors/Background/bg-error-primary', text: 'Colors/Text/text-error-primary' },
};

const TierChip = ({ tier }: { tier: SchoolTier }) => (
  <Box
    component="span"
    sx={({ backrs, typography }) => ({
      ...typography['text-xs-semibold'],
      paddingInline: backrs.spacing['spacing-md'],
      paddingBlock: backrs.spacing['spacing-xxs'],
      borderRadius: backrs.radius['radius-full'],
      backgroundColor: backrs.colors[TIER_COLORS[tier].bg],
      color: backrs.colors[TIER_COLORS[tier].text],
    })}
  >
    {STRINGS.tierLabels[tier]}
  </Box>
);

const RangeCell = ({
  range,
  notReportedTooltip,
}: {
  range: MidRange;
  notReportedTooltip: string;
}) => {
  if (range) {
    return (
      <Typography variant="text-sm-regular" sx={rangeTextSx}>
        {STRINGS.range(range.low, range.high)}
      </Typography>
    );
  }

  return (
    <Tooltip title={notReportedTooltip}>
      <Typography variant="text-sm-regular" sx={notReportedSx}>
        {STRINGS.notReported}
      </Typography>
    </Tooltip>
  );
};

export const SchoolFitTable = ({ rows, currentScore, test }: SchoolFitTableProps) => {
  return (
    <Box sx={wrapperSx} id="booster-school-fit-table">
      <Box sx={scrollSx}>
        <Box sx={gridSx} role="table">
          <Box sx={rowSx} role="row">
            <Box sx={headCellSx} role="columnheader">
              {STRINGS.columnSchool}
            </Box>
            <Box sx={headCellSx} role="columnheader">
              {STRINGS.columnAct}
            </Box>
            <Box sx={headCellSx} role="columnheader">
              {STRINGS.columnSat}
            </Box>
            <Box sx={headCellSx} role="columnheader">
              {STRINGS.columnYourScore(test.toUpperCase())}
            </Box>
            <Box sx={headCellSx} role="columnheader">
              {STRINGS.columnStatus}
            </Box>
          </Box>

          {rows.map((row) => (
            <Box key={row.name} sx={rowSx} role="row">
              <Box sx={bodyCellSx} role="cell">
                <Typography variant="text-sm-medium" sx={nameCellSx}>
                  {row.name || STRINGS.unknownSchool}
                </Typography>
              </Box>
              <Box sx={bodyCellSx} role="cell">
                {row.isPending ? (
                  <Skeleton variant="text" width="70%" />
                ) : (
                  <RangeCell
                    range={row.computed?.actMid50 ?? null}
                    notReportedTooltip={STRINGS.actNotReportedTooltip}
                  />
                )}
              </Box>
              <Box sx={bodyCellSx} role="cell">
                {row.isPending ? (
                  <Skeleton variant="text" width="70%" />
                ) : (
                  <RangeCell
                    range={row.computed?.satMid50 ?? null}
                    notReportedTooltip={STRINGS.satNotReportedTooltip}
                  />
                )}
              </Box>
              <Box sx={bodyCellSx} role="cell">
                <Typography variant="text-sm-regular" sx={rangeTextSx}>
                  {currentScore ?? STRINGS.emptyScore}
                </Typography>
              </Box>
              <Box sx={bodyCellSx} role="cell">
                {row.isPending ? (
                  <Skeleton variant="text" width="70%" />
                ) : row.computed?.tier ? (
                  <TierChip tier={row.computed.tier} />
                ) : (
                  <Typography variant="text-sm-regular" sx={notReportedSx}>
                    {STRINGS.emptyScore}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      <Typography variant="text-xs-regular" sx={disclaimerSx}>
        {STRINGS.disclaimer}
      </Typography>
    </Box>
  );
};

const wrapperSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
});

const scrollSx: SxProps<Theme> = {
  overflowX: 'auto',
};

const gridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: '1.6fr 1fr 1fr 0.9fr 1fr',
  minWidth: '480px',
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  borderRadius: backrs.radius['radius-md'],
  overflow: 'hidden',
});

// The grid tracks are on the parent, so row wrappers must not form their own box.
const rowSx: SxProps<Theme> = {
  display: 'contents',
};

const headCellSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-xs-semibold'],
  display: 'flex',
  alignItems: 'center',
  paddingInline: backrs.spacing['spacing-lg'],
  paddingBlock: backrs.spacing['spacing-md'],
  backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const bodyCellSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  paddingInline: backrs.spacing['spacing-lg'],
  paddingBlock: backrs.spacing['spacing-md'],
  borderTop: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
});

const nameCellSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const rangeTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const notReportedSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-quaternary'],
  fontStyle: 'italic',
});

const disclaimerSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});
